// @flow
// The record editor's toolbar buttons (RichTextEditor), here in the app's bundle so the field shows its toolbar as it
// will be while the editor's code loads (RecordTextField's Loading), with nothing moving when it is in.
import React from 'react';
import { Icon } from '@elements';
import classNames from 'classnames';

// The highlighter first: what doctors mark most. Each with its shortcut (keys: the editor's keymap names).
export const TOOLBAR_BUTTONS = [
  {
    key: 'highlight', icon: 'ink_highlighter', label: 'label/editor/highlight', keys: ['Shift', 'H'],
  },
  {
    key: 'bold', icon: 'format_bold', label: 'label/editor/bold', keys: ['B'],
  },
  {
    key: 'bulletList', icon: 'format_list_bulleted', label: 'label/editor/bullet_list', keys: ['Shift', '8'],
  },
  {
    key: 'orderedList', icon: 'format_list_numbered', label: 'label/editor/ordered_list', keys: ['Shift', '7'],
  },
];

const isMac = () => /Mac|iPhone|iPad/.test(String(window.navigator && window.navigator.platform));

// As people read it (Ctrl+Shift+H, ⌘⇧H) and as aria-keyshortcuts wants it (Control+Shift+H, Meta+Shift+H)
const shortcutText = (keys: Array<string>, mac: boolean): string => (
  mac ? ['⌘', ...keys.map((key) => (key === 'Shift' ? '⇧' : key))].join('') : ['Ctrl', ...keys].join('+')
);
const shortcutAria = (keys: Array<string>, mac: boolean): string => [mac ? 'Meta' : 'Control', ...keys].join('+');

// A tap or a click on the toolbar never takes the focus from the text: the keyboard stays open and the selection
// stays (a pointerdown's default would give the button the focus: on a phone through the mouse events it makes)
const keepFocus = (event: Object) => event.preventDefault();

type Props = {
  _: Function,
  active?: { [string]: boolean },
  disabled?: boolean,
  onRun?: (key: string) => void,
  toolbarRef?: Object,
};

export const ToolbarButtons = ({
  _, active = {}, disabled, onRun, toolbarRef,
}: Props) => {
  const mac = isMac();
  return (
    <div
      ref={toolbarRef}
      className={classNames('recordEditorToolbar', { disabled })}
      role="toolbar"
      aria-label={_('label/editor/toolbar')}
      aria-disabled={disabled || undefined}
      onPointerDown={keepFocus}
      onMouseDown={keepFocus}
    >
      {TOOLBAR_BUTTONS.map((button) => {
        const label = _(button.label);
        return (
          <button
            key={button.key}
            type="button"
            className={classNames('recordEditorButton', button.key, { active: active[button.key] })}
            aria-pressed={Boolean(active[button.key])}
            aria-label={label}
            aria-keyshortcuts={shortcutAria(button.keys, mac)}
            title={`${label} (${shortcutText(button.keys, mac)})`}
            disabled={disabled}
            onPointerDown={keepFocus}
            onMouseDown={keepFocus}
            onClick={onRun ? () => onRun(button.key) : undefined}
          >
            <Icon iconName={button.icon} size={22} />
          </button>
        );
      })}
    </div>
  );
};
